import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { AdminProvider } from "./pages/AdminPage/AdminContext";
import {
  AuthProvider,
  ProtectedRoute,
  getDashboardByRole,
  useAuth,
} from "./utils/auth";

// Layout components
import Footer from "./components/Layout/Footer";
import Navbar from "./components/Layout/Navbar";

// Page components
import InventoryOverview from "./components/Inventory/InventoryOverview";
import DonationManagement from "./components/Management/DonationManagement";
import EmergencyManagementPage from "./components/Management/EmergencyManagement";
import UsersPage from "./components/Management/UserManagement";
import ProfileEdit from "./components/profile/ProfileEdit";
import SettingsPage from "./components/Setting/SettingPage";
import AppointmentBookingPage from "./pages/Appointment/AppointmentBookingPage";
import Blog from "./pages/Blog/Blog";
import Contact from "./pages/Contact/Contact";
import AdminDashboard from "./pages/Dashboard/AdminDashboard";
import MemberDashboard from "./pages/Dashboard/MemberDashboard";
import StaffDashboard from "./pages/Dashboard/StaffDashboard";
import EmergencyRequestPage from "./pages/Emergency/EmergencyRequestPage";
import AboutPage from "./pages/Infor/AboutPage";
import BloodTypeInfoPage from "./pages/Infor/BloodTypeInfoPage";
import HomePage from "./pages/Infor/HomePage";
import ForgotPassword from "./pages/Login/ForgotPassword";
import LoginPage from "./pages/Login/LoginPage";
import RegisterPage from "./pages/Login/RegisterPage";

// Main App component with routing
const AppRoutes: React.FC = () => {
  const { user, isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/blood-types" element={<BloodTypeInfoPage />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<Blog />} />
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={getDashboardByRole(user!.role)} />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          isAuthenticated ? (
            <Navigate to={getDashboardByRole(user!.role)} />
          ) : (
            <ForgotPassword />
          )
        }
      />
      <Route
        path="/register"
        element={
          isAuthenticated ? (
            <Navigate to={getDashboardByRole(user!.role)} />
          ) : (
            <RegisterPage />
          )
        }
      />
      <Route path="/emergency-request" element={<EmergencyRequestPage />} />

      {/* Protected routes */}
      <Route
        path="/member-dashboard"
        element={
          <ProtectedRoute allowedRoles={["member", "admin"]}>
            <MemberDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-dashboard"
        element={
          <ProtectedRoute allowedRoles={["staff", "admin"]}>
            <StaffDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminProvider>
              <AdminDashboard />
            </AdminProvider>
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory-overview"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminProvider>
              <InventoryOverview />
            </AdminProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminProvider>
              <UsersPage />
            </AdminProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/donation-management"
        element={
          <ProtectedRoute allowedRoles={["admin", "staff"]}>
            <AdminProvider>
              <DonationManagement />
            </AdminProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/emergency"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminProvider>
              <EmergencyManagementPage />
            </AdminProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminProvider>
              <SettingsPage />
            </AdminProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointment/newAppointment"
        element={
          <ProtectedRoute allowedRoles={["member", "staff", "admin"]}>
            <AppointmentBookingPage />
          </ProtectedRoute>
        }
      />
      <Route path="/profile-settings" element={<ProfileEdit />} />
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <main className="flex-grow">
            <AppRoutes />
          </main>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
